"use client";

import { cn } from "@repo/ui/lib/utils";
import { Button } from "@repo/ui/components/ui/button";
import { useEffect, useRef, useState } from "react";

// Share (plan 2.13): the phone's own share sheet where the browser has one
// (it lists Eitaa and Bale when installed); otherwise a small menu. Links
// carry utm_source=share&utm_medium=<app>; the page's canonical drops them.
// Eitaa and Bale web share links are left out until their official URL
// formats are confirmed.
export function ShareButton({ url, title }: { url: string; title: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const tagged = (medium: string) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("utm_source", "share");
    u.searchParams.set("utm_medium", medium);
    return u.href;
  };

  const share = async () => {
    if (typeof navigator.share === "function" && window.matchMedia("(pointer: coarse)").matches) {
      await navigator.share({ title, url: tagged("native") }).catch(() => undefined);
      return;
    }
    setOpen((v) => !v);
  };

  const targets = [
    { label: "تلگرام", href: (u: string) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(title)}`, medium: "telegram" },
    { label: "واتساپ", href: (u: string) => `https://wa.me/?text=${encodeURIComponent(`${title} ${u}`)}`, medium: "whatsapp" },
    { label: "ایکس", href: (u: string) => `https://x.com/intent/post?url=${encodeURIComponent(u)}&text=${encodeURIComponent(title)}`, medium: "x" },
  ];

  return (
    <div ref={rootRef} className="relative">
      <Button variant="ghost" onClick={() => void share()} aria-expanded={open} className="bg-background hover:bg-muted font-medium shadow-sm">
        اشتراک‌گذاری
      </Button>
      <div
        className={cn(
          "bg-background absolute end-0 top-full z-30 mt-2 w-44 flex-col overflow-hidden rounded-2xl py-1 text-sm shadow-md",
          open ? "flex" : "hidden",
        )}
      >
        {targets.map((t) => (
          <a
            key={t.medium}
            href={open ? t.href(tagged(t.medium)) : "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:bg-muted px-4 py-2"
            onClick={() => setOpen(false)}
          >
            {t.label}
          </a>
        ))}
        <button
          type="button"
          className="hover:bg-muted px-4 py-2 text-start"
          onClick={async () => {
            await navigator.clipboard.writeText(tagged("copy")).catch(() => undefined);
            setCopied(true);
            setTimeout(() => setCopied(false), 1_500);
          }}
        >
          {copied ? "کپی شد" : "کپی لینک"}
        </button>
      </div>
    </div>
  );
}
